import type { AuthenticatedUser } from "@/models/authenticated_user";
import { differenceInDays, format, startOfDay } from "date-fns";

import { type SubscriptionResponse } from "../api";
import { Utility } from "./utility";

export enum PlanType {
  PRO = "pro",
  ENTERPRISE = "enterprise",
}

export interface Subscription extends SubscriptionResponse {}

export const SUBSCRIPTION_DEFAULT_AGENT_NAME = "Hope";

export class Subscription extends Utility<Subscription> {
  constructor(init?: Partial<SubscriptionResponse>) {
    super();
    Object.assign(this, init);
  }
  get local_created_at(): Date | null {
    if (this.created_at === null) return null;
    return this.get_local_time(this.created_at);
  }

  get local_created_at_date_string(): string | null {
    if (this.local_created_at === null) return null;
    return format(this.local_created_at, "MMM d, yyyy");
  }

  get free_trial_days_remaining(): number | null {
    if (!this.free_trial_end_date) return null;

    const endDate = new Date(this.free_trial_end_date);
    const today = new Date();

    return Math.ceil(differenceInDays(startOfDay(endDate), startOfDay(today)));
  }

  get free_trial_expired(): boolean {
    return (
      this.free_trial_days_remaining !== null &&
      this.free_trial_days_remaining < 0
    );
  }

  get isPro(): boolean {
    return !this.free_trial_expired && this.plan_type === PlanType.PRO;
  }

  get isEnterprise(): boolean {
    return !this.free_trial_expired && this.plan_type === PlanType.ENTERPRISE;
  }

  get isUpgradable(): boolean {
    return this.isPro;
  }

  get canViewRanking(): boolean {
    return this.isPro || this.isEnterprise;
  }

  get canInviteAllCandidates(): boolean {
    return this.isEnterprise;
  }

  get canInviteTop10Candidates(): boolean {
    return this.isPro;
  }

  canInviteCandidate(candidateRank: number | null): boolean {
    if (this.canInviteAllCandidates) {
      return true;
    } else if (this.canInviteTop10Candidates) {
      if (candidateRank === null) return false;
      return candidateRank <= 10;
    }
    return false;
  }

  static getPlanLabel(subscription?: Subscription): string {
    if (subscription?.isEnterprise) return "Enterprise Plan";
    if (subscription?.isPro) return "Pro Plan";
    return "Free Plan";
  }

  static getUpgradeUrl(
    subscription?: Subscription,
    user?: AuthenticatedUser,
  ): string {
    const nextPlan = subscription?.isPro ? "Enterprise" : "Pro";
    return `https://docs.google.com/forms/d/e/1FAIpQLSc4K0nfg8PjOPoB9EYQBegh6ivwTnr_H1jO_uRCy_N_4cfDpQ/viewform?usp=pp_url&entry.55441967=${encodeURIComponent(user?.email || "")}&entry.204715666=${encodeURIComponent(user?.name || "")}&entry.1517171965=${encodeURIComponent(nextPlan)}`;
  }
}
